import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Download, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";
import { openBrandedPrintWindow } from "@/lib/documentTemplate";

const fmt = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");
const statusColors = { paid: "bg-accent/20 text-accent", pending: "bg-primary/15 text-primary", overdue: "bg-destructive/15 text-destructive" };

const empty = { client_id: "", project_id: "", amount: 0, tax: 0, status: "pending", due_date: "", notes: "" };

export default function Invoices() {
  const [rows, setRows] = useState([]);
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);

  const load = () => api.get("/invoices").then((r) => setRows(r.data));
  useEffect(() => {
    load();
    api.get("/clients").then((r) => setClients(r.data));
    api.get("/projects").then((r) => setProjects(r.data));
  }, []);

  const openNew = () => { setForm(empty); setEditId(null); setOpen(true); };
  const openEdit = (r) => {
    setForm({
      client_id: r.client_id || "", project_id: r.project_id || "",
      amount: Number(r.amount || 0), tax: Number(r.tax || 0),
      status: r.status || "pending", due_date: r.due_date || "", notes: r.notes || "",
    });
    setEditId(r.id); setOpen(true);
  };
  const save = async () => {
    try {
      if (editId) { await api.patch(`/invoices/${editId}`, form); toast.success("Invoice updated"); }
      else { await api.post("/invoices", form); toast.success("Invoice created"); }
      setOpen(false); setForm(empty); setEditId(null); load();
    } catch { toast.error("Failed"); }
  };
  const setStatus = async (r, s) => { await api.patch(`/invoices/${r.id}`, { status: s }); load(); };
  const remove = async (id) => { if (!window.confirm("Delete invoice?")) return; await api.delete(`/invoices/${id}`); load(); toast.success("Deleted"); };

  const clientName = (id) => clients.find((c) => c.id === id)?.name || "—";
  const projectName = (id) => projects.find((p) => p.id === id)?.name || "—";

  const printInvoice = (inv) => {
    const rupee = (n) => `₹${Number(n||0).toLocaleString("en-IN")}`;
    const body = `
      <div style="display:flex;justify-content:space-between;gap:24px;margin-bottom:12px">
        <div style="flex:1"><div style="font-size:9px;letter-spacing:1.5px;text-transform:uppercase;color:#73706A">Billed to</div>
          <div style="font-size:15px;font-weight:600;margin-top:4px">${clientName(inv.client_id)}</div>
          <div style="font-size:11px;color:#73706A;margin-top:2px">Project: ${projectName(inv.project_id)}</div>
        </div>
        <div style="text-align:right">
          <div style="font-size:9px;letter-spacing:1.5px;text-transform:uppercase;color:#73706A">Due Date</div>
          <div style="font-size:13px;font-weight:600;margin-top:4px">${inv.due_date || "—"}</div>
          <div style="margin-top:6px;display:inline-block;padding:3px 10px;border-radius:99px;font-size:9px;letter-spacing:1.5px;text-transform:uppercase;background:${inv.status==="paid"?"#DCFCE7":inv.status==="overdue"?"#FEE2E2":"#FEF3C7"};color:${inv.status==="paid"?"#166534":inv.status==="overdue"?"#991B1B":"#92400E"}">${inv.status}</div>
        </div>
      </div>
      <table><thead><tr><th>Description</th><th class="right" style="width:180px">Amount</th></tr></thead>
      <tbody><tr><td>Professional services — ${projectName(inv.project_id)}</td><td class="right">${rupee(inv.amount)}</td></tr>
      <tr><td>GST / Tax</td><td class="right">${rupee(inv.tax)}</td></tr></tbody></table>
      <div style="margin-top:12px;margin-left:auto;width:280px">
        <div style="display:flex;justify-content:space-between;padding:12px 0;border-top:2px solid #1A1918;font-weight:700;font-size:16px"><span>TOTAL DUE</span><span>${rupee(inv.total)}</span></div>
      </div>
      ${inv.notes ? `<div style="margin-top:20px;padding:12px;background:#F7F5F0;border-radius:6px;font-size:11px;color:#3A3936"><b style="text-transform:uppercase;font-size:9px;letter-spacing:1.5px;color:#73706A">Notes</b><div style="margin-top:6px">${inv.notes.replace(/\n/g,"<br>")}</div></div>` : ""}
    `;
    openBrandedPrintWindow(body, {
      subject: `Invoice ${inv.number}`,
      title: "Tax Invoice",
      number: inv.number,
      date: new Date(inv.created_at).toLocaleDateString("en-IN", { dateStyle: "medium" }),
    });
  };

  return (
    <div className="space-y-6" data-testid="invoices-page">
      <div className="flex justify-between items-end">
        <div>
          <div className="text-[11px] uppercase tracking-[0.25em] text-muted-foreground mb-1">Finance</div>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold">Invoices</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button onClick={openNew} data-testid="new-invoice-btn" className="bg-primary text-primary-foreground"><Plus className="w-4 h-4 mr-1.5" />New Invoice</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{editId ? "Edit Invoice" : "Create Invoice"}</DialogTitle><DialogDescription>Update client, project, amount, tax, due date and status.</DialogDescription></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
                <SelectTrigger><SelectValue placeholder="Client" /></SelectTrigger>
                <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={form.project_id} onValueChange={(v) => setForm({ ...form, project_id: v })}>
                <SelectTrigger><SelectValue placeholder="Project" /></SelectTrigger>
                <SelectContent>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
              <Input type="number" placeholder="Amount" value={form.amount} onChange={(e) => setForm({ ...form, amount: Number(e.target.value) })} />
              <Input type="number" placeholder="Tax" value={form.tax} onChange={(e) => setForm({ ...form, tax: Number(e.target.value) })} />
              <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{["pending","paid","overdue"].map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
              </Select>
              <Input className="col-span-2" placeholder="Notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
            <Button onClick={save} data-testid="save-invoice-btn" className="bg-primary text-primary-foreground">{editId ? "Update" : "Save"}</Button>
          </DialogContent>
        </Dialog>
      </div>

      <div className="rounded-2xl bg-card border border-border/60 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
            <tr><th className="text-left py-3 px-4">Number</th><th className="text-left">Client</th><th className="text-left">Project</th><th className="text-right">Amount</th><th className="text-right">Total</th><th>Status</th><th></th></tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} data-testid={`invoice-${r.id}`} className="border-t border-border/50 hover:bg-muted/30 transition-colors">
                <td className="py-3 px-4 font-mono text-xs">{r.number}</td>
                <td>{clientName(r.client_id)}</td>
                <td>{projectName(r.project_id)}</td>
                <td className="text-right">{fmt(r.amount)}</td>
                <td className="text-right font-medium">{fmt(r.total)}</td>
                <td>
                  <Select value={r.status} onValueChange={(v) => setStatus(r, v)}>
                    <SelectTrigger className={`h-7 w-28 text-[11px] ${statusColors[r.status] || ""}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{["pending","paid","overdue"].map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
                  </Select>
                </td>
                <td className="pr-4 text-right whitespace-nowrap">
                  <button onClick={() => openEdit(r)} data-testid={`edit-invoice-${r.id}`} className="p-1.5 hover:bg-muted/60 rounded text-primary" title="Edit"><Pencil className="w-3.5 h-3.5" /></button>
                  <button onClick={() => printInvoice(r)} data-testid={`download-invoice-${r.id}`} className="p-1.5 hover:bg-muted/60 rounded" title="Download PDF"><Download className="w-3.5 h-3.5" /></button>
                  <button onClick={() => remove(r.id)} data-testid={`delete-invoice-${r.id}`} className="p-1.5 hover:bg-muted/60 rounded text-destructive" title="Delete"><Trash2 className="w-3.5 h-3.5" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
